/**
 * The header's Upload button (section 5): a video or a picture from any screen. While this browser
 * uploads, the button shows how far along it is; the dialog lists every upload of this visit with
 * its state. What is uploaded lands in Files, ready to choose in any item's Video or Artwork.
 */

import { useRef, useState } from "react";
import Button from "@web-ui/components/Button";
import Field from "@web-ui/components/Field";
import Modal from "@web-ui/components/Modal";
import Notice from "@web-ui/components/Notice";
import Segmented from "@web-ui/components/Segmented";
import Select from "@web-ui/components/Select";
import { useStudio } from "@/shell/studio";
import { explainMediaError } from "./errors";
import { ImageCropUpload } from "./image-upload";
import { useUploads } from "./upload-center";
import { UploadStateLine } from "./upload-state";
import { ENCODER_LINE, videoState } from "./states";

export const LADDERS = [
  { id: 0, name: "Standard (720p)", value: "standard", note: "About 1.5 GB per hour. Right for most videos." },
  { id: 1, name: "High (1080p)", value: "hero", note: "About 2.9 GB per hour. For films and featured videos." },
  { id: 2, name: "Light (480p)", value: "preview", note: "About 0.7 GB per hour. For trailers and loops." },
];

export function UploadButton() {
  const { uploads } = useUploads();
  const [open, setOpen] = useState(false);
  const sending = uploads.filter((u) => !u.error && u.progress < 1);
  const pct = sending.length ? Math.floor((sending.reduce((n, u) => n + u.progress, 0) / sending.length) * 100) : null;
  return (
    <>
      <Button type="button" variant="tertiary" size="sm" icon="upload" onClick={() => setOpen(true)} aria-label={pct === null ? "Upload a video or picture" : `Uploading ${pct}%. Show uploads`}>
        <span className="max-md:hidden">{pct === null ? "Upload" : `Uploading ${pct}%`}</span>
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} size="md">
        <UploadDialog onClose={() => setOpen(false)} />
      </Modal>
    </>
  );
}

function UploadDialog({ onClose }: { onClose: () => void }) {
  const { client, pack } = useStudio();
  const { uploads, startVideo, cancel, dismiss, stateOf, refresh } = useUploads();
  const [kind, setKind] = useState<"video" | "image">("video");
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [ladder, setLadder] = useState(LADDERS[pack.vertical === "film" ? 1 : 0]!);
  const [message, setMessage] = useState<{ tone: "positive" | "critical"; text: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const start = async () => {
    if (!file) return;
    setMessage(null);
    try {
      await startVideo(file, title.trim() || file.name.replace(/\.[^.]+$/, ""), ladder.value);
      setFile(null); setTitle(""); if (input.current) input.current.value = "";
    } catch (e) { setMessage({ tone: "critical", text: explainMediaError(e) }); }
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-h5">Upload</h2>
      <p className="text-body-2 text-t-secondary">It goes to Files. Use it from any item's Video or Artwork section, or upload there directly.</p>
      <Segmented label="What are you uploading?" options={[{ id: "video", label: "A video" }, { id: "image", label: "A picture" }]} value={kind} onChange={(v) => setKind(v)} />
      {message ? <Notice tone={message.tone} onDismiss={() => setMessage(null)}>{message.text}</Notice> : null}
      {kind === "video" ? (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-body-2">Video file
            <input ref={input} type="file" accept="video/*" className="text-body-2" onChange={(e) => { const f = e.target.files?.[0] ?? null; setFile(f); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, "")); }} />
          </label>
          <Field label="Name in Files" value={title} onChange={(e) => setTitle(e.target.value)} />
          <div className="flex flex-col gap-1">
            <Select label="Quality" value={ladder} options={LADDERS} onChange={(o) => setLadder(LADDERS[o.id]!)} />
            <p className="text-caption text-t-secondary">{ladder.note} {ENCODER_LINE}</p>
          </div>
          <div><Button type="button" variant="primary" disabled={!file} onClick={() => void start()}>Start the upload</Button></div>
        </div>
      ) : (
        <ImageCropUpload client={client} onDone={(_feed, _asset, existing) => { setMessage({ tone: "positive", text: existing ? "That picture was already in Files with this crop." : "The picture is in Files." }); void refresh(); }} onError={(e) => setMessage({ tone: "critical", text: explainMediaError(e) })} />
      )}
      {uploads.length ? (
        <section className="flex flex-col gap-2 border-t border-s-subtle pt-4" aria-label="Uploads">
          <h3 className="text-sub-title-2">This visit's uploads</h3>
          <ul className="flex flex-col gap-3">
            {uploads.map((u) => {
              const state = u.assetId ? stateOf(u.assetId) ?? videoState(null, u) : videoState(null, u);
              const running = !u.error && u.progress < 1;
              return (
                <li key={u.key} className="flex items-start gap-3 rounded-2xl border border-s-subtle p-3">
                  <div className="flex min-w-0 grow flex-col gap-1">
                    <span className="truncate text-body-2">{u.title}</span>
                    <UploadStateLine state={state} />
                  </div>
                  {running ? <Button type="button" size="xs" variant="ghost" onClick={() => cancel(u.key)}>Cancel</Button> : <Button type="button" size="xs" variant="ghost" onClick={() => dismiss(u.key)}>Hide</Button>}
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
      <div><Button type="button" variant="outline" onClick={onClose}>Close</Button></div>
    </div>
  );
}
